import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, BellOff, CheckCircle2, Clock, FileText, Inbox, RefreshCw, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { overviewAPI } from '../../lib/api';

/**
 * "I have just opened Accountrix — what happened overnight, and what needs me?"
 *
 * Two sections, NEEDS ATTENTION first, and they are never interleaved. The moment "12 new bilag"
 * and "ingest failing" share a row, the accountant has to do the triage this screen exists to do.
 *
 * Attention is a list of PROBLEMS, not of clients: one client with three problems shows three
 * rows. A per-client shape gives every client equal visual weight, which is exactly wrong when one
 * of fifty needs you.
 */

function windowLabel(w) {
    if (!w) return '';
    if (w.reason === 'first_visit') return 'seneste 24 timer';
    if (w.reason === 'recent_relogin') return 'seneste 24 timer (du var her lige før)';
    if (w.reason === 'capped_long_absence') return `seneste 14 dage (du har været væk længere)`;
    const h = w.hours || 0;
    if (h < 36) return `siden dit sidste besøg for ${Math.round(h)} timer siden`;
    return `siden dit sidste besøg for ${Math.round(h / 24)} dage siden`;
}

function Stat({ icon: Icon, label, value, suffix, testid }) {
    return (
        <Card className="stat-card" data-testid={testid}>
            <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                        <p className="text-2xl font-semibold leading-none">{value}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {label}{suffix ? <span className="block">{suffix}</span> : null}
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function AttentionRow({ row, onAcknowledge, busy }) {
    const navigate = useNavigate();
    const isError = row.severity === 'error';
    // stuck / needs_type carry neither flag: one reports a fault in Accountrix, the other blocks
    // approval. Offering a dismiss button for those would invite silencing the wrong thing.
    const canSilence = row.can_acknowledge || row.can_snooze;
    return (
        <div
            data-testid={`attention-${row.kind}-${row.tenant_id}`}
            className={`flex items-start justify-between gap-3 rounded border p-3 ${
                isError ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}
        >
            <div className="min-w-0 flex items-start gap-2">
                <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${isError ? 'text-red-700' : 'text-amber-700'}`} />
                <div className="min-w-0">
                    <p className={`text-sm font-medium ${isError ? 'text-red-900' : 'text-amber-900'}`}>
                        {row.name}
                    </p>
                    <p className={`text-xs mt-0.5 break-words ${isError ? 'text-red-800' : 'text-amber-800'}`}>
                        {row.detail}
                    </p>
                    {row.digest_regardless && (
                        <p className="text-[11px] mt-1 text-red-700">
                            Sendes i den daglige mail uanset udsættelse.
                        </p>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => navigate(row.href || '/app/companies')}>
                    Åbn
                </Button>
                {canSilence && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy}
                        title={row.can_snooze ? 'Udsæt i 24 timer' : 'Kvittér — vises igen hvis forholdet ændrer sig'}
                        data-testid={`attention-ack-${row.kind}-${row.tenant_id}`}
                        onClick={() => onAcknowledge(row)}>
                        <BellOff className="h-3.5 w-3.5 mr-1" />
                        {row.can_snooze ? 'Udsæt' : 'Kvittér'}
                    </Button>
                )}
            </div>
        </div>
    );
}

export default function MorningOverview() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [lastAck, setLastAck] = useState(null);   // supports undo — acking must never be one-way

    const load = useCallback(async () => {
        try {
            const res = await overviewAPI.get();
            setData(res.data);
        } catch (e) {
            toast.error('Kunne ikke hente oversigten');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const acknowledge = async (row) => {
        setBusy(true);
        try {
            await overviewAPI.acknowledge({
                kind: row.kind, scope_key: row.scope_key || row.tenant_id,
                tenant_id: row.tenant_id, fingerprint: row.fingerprint,
            });
            setLastAck({ kind: row.kind, scope_key: row.scope_key || row.tenant_id, name: row.name });
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(d?.message || d || 'Kunne ikke kvittere');
        } finally {
            setBusy(false);
        }
    };

    const undo = async () => {
        if (!lastAck) return;
        setBusy(true);
        try {
            await overviewAPI.unacknowledge(lastAck.kind, lastAck.scope_key);
            setLastAck(null);
            await load();
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="p-8 text-sm text-muted-foreground" data-testid="overview-loading">Henter oversigt…</div>;
    }
    if (!data) return null;

    const t = data.totals || {};
    const attention = data.attention || [];
    const newWork = data.new_work || [];

    return (
        <div className="space-y-6" data-testid="morning-overview">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-semibold">Overblik</h1>
                    {/* The window is always stated. An unlabelled "12 new" is not checkable. */}
                    <p className="text-sm text-muted-foreground mt-1" data-testid="overview-window">
                        {windowLabel(data.window)} · {data.clients} {data.clients === 1 ? 'klient' : 'klienter'}
                    </p>
                    {/* Work reported back, composed server-side from THIS payload — no AI call, so
                        every clause is one of the numbers rendered below it. Deliberately placed
                        above the stat cards: it is the summary they are the breakdown of. */}
                    {data.narrative && (
                        <p className="text-sm mt-2 max-w-2xl" data-testid="overview-narrative">
                            {data.narrative}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {lastAck && (
                        <Button size="sm" variant="outline" onClick={undo} disabled={busy}
                            data-testid="overview-undo">
                            <Undo2 className="h-3.5 w-3.5 mr-1" /> Fortryd
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={load} disabled={busy}
                        data-testid="overview-refresh">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* B FIRST. What needs you outranks what arrived. */}
            <Card data-testid="overview-attention-card">
                <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                        Kræver din opmærksomhed
                        {attention.length > 0 && (
                            <span className="rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 text-[11px]">
                                {attention.length}
                            </span>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                    {attention.length === 0 ? (
                        <p className="text-sm text-muted-foreground flex items-center gap-2"
                            data-testid="overview-attention-empty">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            Intet kræver din opmærksomhed.
                        </p>
                    ) : attention.map((row, i) => (
                        <AttentionRow key={`${row.kind}-${row.tenant_id}-${i}`} row={row}
                            onAcknowledge={acknowledge} busy={busy} />
                    ))}
                </CardContent>
            </Card>

            {/* A. New work — flow and backlog kept visually distinct. */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat icon={Inbox} label="nye bilag" value={t.new_documents ?? 0}
                    suffix={t.ingested ? `heraf ${t.ingested} hentet automatisk` : null}
                    testid="stat-new-documents" />
                <Stat icon={FileText} label="afventer gennemgang" value={t.awaiting_review ?? 0}
                    testid="stat-awaiting-review" />
                <Stat icon={CheckCircle2} label="klar til levering" value={t.ready_to_deliver ?? 0}
                    // An age qualifier, because "7 ready" and "7 ready, oldest 84 days" ask for
                    // different responses and only the second is honest about a backlog.
                    suffix={t.ready_to_deliver ? `ældste ${t.oldest_ready_days ?? 0} dage` : null}
                    testid="stat-ready-to-deliver" />
                <Stat icon={Clock} label="kræver opmærksomhed" value={t.attention ?? 0}
                    suffix={t.attention_errors ? `${t.attention_errors} haster` : null}
                    testid="stat-attention" />
            </div>

            <Card data-testid="overview-newwork-card">
                <CardHeader className="py-3">
                    <CardTitle className="text-sm">Nyt arbejde pr. klient</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 overflow-x-auto">
                    {newWork.length === 0 ? (
                        <p className="text-sm text-muted-foreground" data-testid="overview-newwork-empty">
                            Intet nyt siden sidst.
                        </p>
                    ) : (
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-muted-foreground border-b">
                                    <th className="py-1 pr-4 font-medium">Klient</th>
                                    <th className="py-1 pr-4 font-medium">Nye</th>
                                    <th className="py-1 pr-4 font-medium">Til gennemgang</th>
                                    <th className="py-1 font-medium">Klar til levering</th>
                                </tr>
                            </thead>
                            <tbody>
                                {newWork.map((w) => (
                                    <tr key={w.tenant_id} className="border-b border-border/40"
                                        data-testid={`newwork-row-${w.tenant_id}`}>
                                        <td className="py-1 pr-4 truncate max-w-[220px]">
                                            <button className="hover:underline text-left"
                                                onClick={() => navigate('/app/companies')}>{w.name}</button>
                                        </td>
                                        <td className="py-1 pr-4">
                                            {w.new_documents}
                                            {w.ingested ? <span className="text-muted-foreground"> ({w.ingested} hentet)</span> : null}
                                        </td>
                                        <td className="py-1 pr-4">{w.awaiting_review}</td>
                                        <td className="py-1">
                                            {w.ready_to_deliver}
                                            {w.ready_to_deliver && w.oldest_ready_days != null ? (
                                                <span className="text-muted-foreground"> · ældste {w.oldest_ready_days} d</span>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
